import { db } from '../firebase';
import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  collection, 
  getDocs, 
  query, 
  orderBy, 
  serverTimestamp,
  increment
} from 'firebase/firestore';

export type PlanType = 'dynamic' | 'template' | 'communication';

export interface StickerEvent {
  id: string;
  planId: string;
  planTitle: string;
  planType: PlanType;
  day: number;
  stickersEarned: number;
  eventType: 'day_completion' | 'plan_completion' | 'bonus' | 'milestone';
  timestamp: Date;
  description: string;
  isBonus?: boolean;
  isRetroactive?: boolean;
}

export interface UserStickerStats {
  totalStickers: number;
  totalPlansCompleted: number;
  totalDaysCompleted: number;
  averageStickersPerPlan: number;
  currentStreak: number;
  longestStreak: number;
  lastActivityDate: Date;
  stickersByPlanType: {
    dynamic: number;
    template: number;
    communication: number;
  };
  recentStickerEvents: StickerEvent[];
  achievements: StickerAchievement[];
}

export interface StickerAchievement {
  id: string;
  name: string;
  description: string;
  icon: string;
  unlockedAt: Date;
  requirements: {
    type: 'total_stickers' | 'plans_completed' | 'streak' | 'days_completed';
    value: number;
  };
}

export interface PlanStickerStats {
  planId: string;
  planTitle: string;
  planType: PlanType;
  totalStickers: number;
  daysCompleted: number;
  totalDays: number;
  completionPercentage: number;
  isCompleted: boolean;
  startedAt: Date;
  completedAt?: Date;
  lastActivityAt: Date;
  stickerEvents: StickerEvent[];
}

export class StickerTrackingService {
  private db = db;

  /**
   * Get localized sticker event description
   */
  private getStickerEventDescription(
    eventType: 'day_completion' | 'plan_completion' | 'bonus' | 'milestone',
    day: number,
    planTitle: string,
    isLastDay: boolean = false,
    userLanguage: 'zh-CN' | 'es' | 'fr' | 'en' = 'en'
  ): string {
    const descriptions = {
      'zh-CN': {
        day_completion: `完成第${day}天 "${planTitle}" (+1)`,
        plan_completion: `完成最终天 "${planTitle}" (+2 奖励贴纸!)`,
        bonus: `完成第${day}天 "${planTitle}" (+1)`,
        milestone: `完成里程碑 "${planTitle}" (+1)`
      },
      'es': {
        day_completion: `Completado día ${day} de "${planTitle}" (+1)`,
        plan_completion: `Completado día final de "${planTitle}" (+2 calcomanías de bonificación!)`,
        bonus: `Completado día ${day} de "${planTitle}" (+1)`,
        milestone: `Completado hito "${planTitle}" (+1)`
      },
      'fr': {
        day_completion: `Jour ${day} terminé "${planTitle}" (+1)`,
        plan_completion: `Dernier jour terminé "${planTitle}" (+2 autocollants bonus!)`,
        bonus: `Jour ${day} terminé "${planTitle}" (+1)`,
        milestone: `Jalon terminé "${planTitle}" (+1)`
      },
      'en': {
        day_completion: `Completed day ${day} of "${planTitle}" (+1)`,
        plan_completion: `Completed final day of "${planTitle}" (+2 bonus stickers!)`,
        bonus: `Completed day ${day} of "${planTitle}" (+1)`,
        milestone: `Completed milestone "${planTitle}" (+1)`
      }
    };

    const eventTypeKey = isLastDay ? 'plan_completion' : eventType;
    return descriptions[userLanguage]?.[eventTypeKey] || descriptions['en'][eventTypeKey];
  }

  /**
   * Award stickers for completing a day
   */
  async awardDayCompletionStickers(
    userId: string,
    planId: string,
    planTitle: string,
    planType: 'dynamic' | 'template' | 'communication',
    day: number,
    totalDays: number,
    isLastDay: boolean = false,
    userLanguage: 'zh-CN' | 'es' | 'fr' | 'en' = 'en'
  ): Promise<{ stickersEarned: number; totalUserStickers: number }> {
    try {
      console.log('🏆 Awarding day completion stickers:', {
        userId,
        planId,
        planTitle,
        day,
        isLastDay
      });

      // Calculate stickers earned (1 for regular day, 2 for final day)
      const stickersEarned = isLastDay ? 2 : 1;

      // Create sticker event
      const stickerEvent: StickerEvent = {
        id: `sticker_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        planId,
        planTitle,
        planType,
        day,
        stickersEarned,
        eventType: isLastDay ? 'plan_completion' : 'day_completion',
        timestamp: new Date(),
        description: this.getStickerEventDescription(
          isLastDay ? 'plan_completion' : 'day_completion',
          day,
          planTitle,
          isLastDay,
          userLanguage
        ),
        isBonus: isLastDay
      };

      // Update user sticker stats
      const userStatsRef = doc(this.db, 'users', userId, 'stats', 'stickers');
      const userStatsDoc = await getDoc(userStatsRef);
      
      let currentStats: UserStickerStats;
      if (userStatsDoc.exists()) {
        currentStats = userStatsDoc.data() as UserStickerStats;
      } else {
        currentStats = this.getDefaultUserStats();
      }

      // Update stats
      console.log('🔢 STICKER CALCULATION DEBUG:', {
        currentTotalStickers: currentStats.totalStickers,
        stickersEarned,
        newTotalStickers: currentStats.totalStickers + stickersEarned,
        currentDaysCompleted: currentStats.totalDaysCompleted,
        newDaysCompleted: currentStats.totalDaysCompleted + 1
      });

      const updatedStats: UserStickerStats = {
        ...currentStats,
        totalStickers: currentStats.totalStickers + stickersEarned,
        totalDaysCompleted: currentStats.totalDaysCompleted + 1,
        lastActivityDate: new Date(),
        stickersByPlanType: {
          ...currentStats.stickersByPlanType,
          [planType]: currentStats.stickersByPlanType[planType] + stickersEarned
        },
        recentStickerEvents: [
          stickerEvent,
          ...currentStats.recentStickerEvents.slice(0, 19) // Keep last 20 events
        ]
      };

      // Update plan completion count if it's the last day
      if (isLastDay) {
        updatedStats.totalPlansCompleted += 1;
      }

      // Calculate streaks
      const today = new Date();
      const lastActivity = new Date(currentStats.lastActivityDate);
      const daysDiff = Math.floor((today.getTime() - lastActivity.getTime()) / (1000 * 60 * 60 * 24));
      
      if (daysDiff === 1) {
        updatedStats.currentStreak += 1;
      } else if (daysDiff > 1) {
        updatedStats.currentStreak = 1;
      }
      
      if (updatedStats.currentStreak > updatedStats.longestStreak) {
        updatedStats.longestStreak = updatedStats.currentStreak;
      }

      // Check for new achievements
      const newAchievements = this.checkAchievements(updatedStats, userLanguage);
      updatedStats.achievements = [...currentStats.achievements, ...newAchievements];

      // Save updated stats
      await setDoc(userStatsRef, updatedStats);
      console.log('💾 Saved updated stats to database:', {
        path: userStatsRef.path,
        totalStickers: updatedStats.totalStickers,
        totalDaysCompleted: updatedStats.totalDaysCompleted
      });

      // Update plan-specific stats
      await this.updatePlanStickerStats(userId, planId, planTitle, planType, day, totalDays, stickersEarned, isLastDay, userLanguage);

      console.log('✅ Stickers awarded successfully:', {
        stickersEarned,
        totalUserStickers: updatedStats.totalStickers,
        newAchievements: newAchievements.length
      });

      return {
        stickersEarned,
        totalUserStickers: updatedStats.totalStickers
      };

    } catch (error) {
      console.error('❌ Error awarding stickers:', error);
      throw new Error('Failed to award stickers');
    }
  }

  /**
   * Retroactively award stickers for completed days that weren't tracked
   * This is useful for users who completed activities before sticker tracking was implemented
   */
  async retroactivelyAwardStickers(userId: string, planId: string, planTitle: string, planType: PlanType, completedDays: number[], userLanguage: 'zh-CN' | 'es' | 'fr' | 'en' = 'en'): Promise<void> {
    try {
      console.log('🔄 RETROACTIVE STICKER AWARDING:', {
        userId,
        planId,
        planTitle,
        planType,
        completedDays
      });

      // Get current user stats
      const currentStats = await this.getUserStickerStats(userId);
      
      // Calculate total stickers to award
      let totalStickersToAward = 0;
      const stickerEvents: StickerEvent[] = [];

      for (const day of completedDays) {
        const isLastDay = day === completedDays[completedDays.length - 1];
        const stickersForDay = isLastDay ? 2 : 1; // Last day gets bonus sticker
        totalStickersToAward += stickersForDay;

        const stickerEvent: StickerEvent = {
          id: `retroactive-${planId}-day-${day}-${Date.now()}`,
          planId,
          planTitle,
          planType,
          day,
          stickersEarned: stickersForDay,
          eventType: 'day_completion',
          description: this.getStickerEventDescription(
            'day_completion',
            day,
            planTitle,
            false,
            userLanguage
          ),
          timestamp: new Date(),
          isRetroactive: true
        };

        stickerEvents.push(stickerEvent);
      }

      console.log('🔄 RETROACTIVE CALCULATION:', {
        totalStickersToAward,
        stickerEvents: stickerEvents.length
      });

      // Update user stats
      const updatedStats: UserStickerStats = {
        ...currentStats,
        totalStickers: currentStats.totalStickers + totalStickersToAward,
        totalDaysCompleted: currentStats.totalDaysCompleted + completedDays.length,
        totalPlansCompleted: currentStats.totalPlansCompleted + 1, // This was a completed plan
        lastActivityDate: new Date(),
        stickersByPlanType: {
          ...currentStats.stickersByPlanType,
          [planType]: currentStats.stickersByPlanType[planType] + totalStickersToAward
        },
        recentStickerEvents: [
          ...stickerEvents,
          ...currentStats.recentStickerEvents.slice(0, 20 - stickerEvents.length) // Keep last 20 events
        ]
      };

      // Save updated stats
      const userStatsRef = doc(this.db, 'users', userId, 'stats', 'stickers');
      await setDoc(userStatsRef, updatedStats);

      console.log('✅ RETROACTIVE STICKERS AWARDED:', {
        totalStickersAwarded: totalStickersToAward,
        newTotalStickers: updatedStats.totalStickers,
        newTotalDaysCompleted: updatedStats.totalDaysCompleted
      });

    } catch (error) {
      console.error('❌ Error awarding retroactive stickers:', error);
      throw new Error('Failed to award retroactive stickers');
    }
  }

  /**
   * Get comprehensive user sticker statistics
   */
  async getUserStickerStats(userId: string): Promise<UserStickerStats> {
    try {
      console.log('📊 getUserStickerStats: Checking for user:', userId);
      const userStatsRef = doc(this.db, 'users', userId, 'stats', 'stickers');
      console.log('📊 getUserStickerStats: Document path:', userStatsRef.path);
      
      const userStatsDoc = await getDoc(userStatsRef);
      console.log('📊 getUserStickerStats: Document exists:', userStatsDoc.exists());
      
      if (userStatsDoc.exists()) {
        const data = userStatsDoc.data() as any;
        console.log('📊 getUserStickerStats: Found existing data:', {
          totalStickers: data.totalStickers,
          totalDaysCompleted: data.totalDaysCompleted,
          totalPlansCompleted: data.totalPlansCompleted,
          lastActivityDate: data.lastActivityDate
        });
        
        // Convert Firestore Timestamps to JavaScript Dates
        const convertedData: UserStickerStats = {
          totalStickers: data.totalStickers || 0,
          totalPlansCompleted: data.totalPlansCompleted || 0,
          totalDaysCompleted: data.totalDaysCompleted || 0,
          averageStickersPerPlan: data.averageStickersPerPlan || 0,
          currentStreak: data.currentStreak || 0,
          longestStreak: data.longestStreak || 0,
          lastActivityDate: data.lastActivityDate?.toDate ? data.lastActivityDate.toDate() : new Date(data.lastActivityDate),
          stickersByPlanType: data.stickersByPlanType || {
            dynamic: 0,
            template: 0,
            communication: 0
          },
          achievements: data.achievements?.map((achievement: any) => ({
            ...achievement,
            unlockedAt: achievement.unlockedAt?.toDate ? achievement.unlockedAt.toDate() : new Date(achievement.unlockedAt)
          })) || [],
          recentStickerEvents: data.recentStickerEvents?.map((event: any) => ({
            ...event,
            timestamp: event.timestamp?.toDate ? event.timestamp.toDate() : new Date(event.timestamp)
          })) || []
        };
        
        return convertedData;
      } else {
        console.log('📊 getUserStickerStats: No existing data, checking for progress documents...');
        
        // Check if user has any progress documents
        const plansRef = collection(this.db, 'users', userId, 'plans');
        const plansSnapshot = await getDocs(plansRef);
        console.log('📊 getUserStickerStats: Found', plansSnapshot.docs.length, 'progress documents');
        
        if (plansSnapshot.docs.length > 0) {
          console.log('📊 getUserStickerStats: User has progress but no sticker tracking data');
          // User has progress but no sticker tracking data - this means they completed activities before sticker tracking was implemented
          console.log('📊 getUserStickerStats: This is expected for existing users');
        }
        
        console.log('📊 getUserStickerStats: Returning default stats');
        return this.getDefaultUserStats();
      }
    } catch (error) {
      console.error('❌ Error getting user sticker stats:', error);
      throw new Error('Failed to retrieve sticker statistics');
    }
  }

  /**
   * Get sticker statistics for all user's plans
   */
  async getPlanStickerStats(userId: string): Promise<PlanStickerStats[]> {
    try {
      // For now, return empty array since we don't have a plans collection
      // This will be populated when users complete roadmaps
      console.log('📊 getPlanStickerStats: Returning empty array for now');
      return [];
    } catch (error) {
      console.error('❌ Error getting plan sticker stats:', error);
      return [];
    }
  }

  /**
   * Get sticker events for a specific plan
   */
  async getPlanStickerEvents(userId: string, planId: string): Promise<StickerEvent[]> {
    try {
      const eventsRef = collection(this.db, 'users', userId, 'plans', planId, 'stickerEvents');
      const eventsSnapshot = await getDocs(query(eventsRef, orderBy('timestamp', 'desc')));
      
      return eventsSnapshot.docs.map(doc => ({
        ...doc.data(),
        timestamp: doc.data().timestamp.toDate()
      })) as StickerEvent[];
      
    } catch (error) {
      console.error('❌ Error getting plan sticker events:', error);
      return [];
    }
  }

  /**
   * Update plan-specific sticker statistics
   */
  private async updatePlanStickerStats(
    userId: string,
    planId: string,
    planTitle: string,
    planType: 'dynamic' | 'template' | 'communication',
    day: number,
    totalDays: number,
    stickersEarned: number,
    isLastDay: boolean,
    userLanguage: 'zh-CN' | 'es' | 'fr' | 'en' = 'en'
  ): Promise<void> {
    try {
      // Create sticker event for this plan
      const stickerEvent: StickerEvent = {
        id: `sticker_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        planId,
        planTitle,
        planType,
        day,
        stickersEarned,
        eventType: isLastDay ? 'plan_completion' : 'day_completion',
        timestamp: new Date(),
        description: this.getStickerEventDescription(
          isLastDay ? 'plan_completion' : 'day_completion',
          day,
          planTitle,
          isLastDay,
          userLanguage
        ),
        isBonus: isLastDay
      };

      // Save sticker event to plan subcollection
      const eventRef = doc(this.db, 'users', userId, 'plans', planId, 'stickerEvents', stickerEvent.id);
      await setDoc(eventRef, {
        ...stickerEvent,
        timestamp: serverTimestamp()
      });

      // Update plan document with latest sticker count
      const planRef = doc(this.db, 'users', userId, 'plans', planId);
      await updateDoc(planRef, {
        totalStickers: increment(stickersEarned),
        lastStickerEarned: serverTimestamp(),
        isCompleted: isLastDay
      });

    } catch (error) {
      console.error('❌ Error updating plan sticker stats:', error);
    }
  }

  /**
   * Get translated achievement text
   */
  private getAchievementText(achievementId: string, userLanguage: 'zh-CN' | 'es' | 'fr' | 'en' = 'en'): { name: string; description: string } {
    const achievementTexts: Record<string, Record<string, { name: string; description: string }>> = {
      'first_sticker': {
        'zh-CN': { name: '第一步', description: '获得了您的第一张贴纸！' },
        'es': { name: 'Primeros Pasos', description: '¡Ganaste tu primera calcomanía!' },
        'fr': { name: 'Premiers Pas', description: 'Vous avez gagné votre premier autocollant !' },
        'en': { name: 'First Steps', description: 'Earned your first sticker!' }
      },
      'sticker_collector_10': {
        'zh-CN': { name: '贴纸收集者', description: '获得了10张贴纸！' },
        'es': { name: 'Coleccionista', description: '¡Ganaste 10 calcomanías!' },
        'fr': { name: 'Collectionneur', description: 'Vous avez gagné 10 autocollants !' },
        'en': { name: 'Sticker Collector', description: 'Earned 10 stickers!' }
      },
      'sticker_master_50': {
        'zh-CN': { name: '贴纸大师', description: '获得了50张贴纸！' },
        'es': { name: 'Maestro de Calcomanías', description: '¡Ganaste 50 calcomanías!' },
        'fr': { name: 'Maître des Autocollants', description: 'Vous avez gagné 50 autocollants !' },
        'en': { name: 'Sticker Master', description: 'Earned 50 stickers!' }
      },
      'sticker_legend_100': {
        'zh-CN': { name: '贴纸传奇', description: '获得了100张贴纸！' },
        'es': { name: 'Leyenda de Calcomanías', description: '¡Ganaste 100 calcomanías!' },
        'fr': { name: 'Légende des Autocollants', description: 'Vous avez gagné 100 autocollants !' },
        'en': { name: 'Sticker Legend', description: 'Earned 100 stickers!' }
      },
      'first_plan': {
        'zh-CN': { name: '计划完成者', description: '完成了您的第一个计划！' },
        'es': { name: 'Finalizador de Planes', description: '¡Completaste tu primer plan!' },
        'fr': { name: 'Finisseur de Plans', description: 'Vous avez terminé votre premier plan !' },
        'en': { name: 'Plan Finisher', description: 'Completed your first plan!' }
      },
      'plan_expert_5': {
        'zh-CN': { name: '计划专家', description: '完成了5个计划！' },
        'es': { name: 'Experto en Planes', description: '¡Completaste 5 planes!' },
        'fr': { name: 'Expert en Plans', description: 'Vous avez terminé 5 plans !' },
        'en': { name: 'Plan Expert', description: 'Completed 5 plans!' }
      },
      'streak_master_7': {
        'zh-CN': { name: '连续大师', description: '连续7天完成计划！' },
        'es': { name: 'Maestro de Racha', description: '¡Completaste planes 7 días seguidos!' },
        'fr': { name: 'Maître de Série', description: 'Vous avez terminé des plans 7 jours de suite !' },
        'en': { name: 'Streak Master', description: 'Completed plans for 7 days straight!' }
      },
      'streak_legend_30': {
        'zh-CN': { name: '连续传奇', description: '连续30天完成计划！' },
        'es': { name: 'Leyenda de Racha', description: '¡Completaste planes 30 días seguidos!' },
        'fr': { name: 'Légende de Série', description: 'Vous avez terminé des plans 30 jours de suite !' },
        'en': { name: 'Streak Legend', description: 'Completed plans for 30 days straight!' }
      }
    };

    return achievementTexts[achievementId]?.[userLanguage] || achievementTexts[achievementId]?.['en'] || { name: 'Achievement', description: 'You earned an achievement!' };
  }

  /**
   * Check for new achievements based on updated stats
   */
  private checkAchievements(stats: UserStickerStats, userLanguage: 'zh-CN' | 'es' | 'fr' | 'en' = 'en'): StickerAchievement[] {
    const newAchievements: StickerAchievement[] = [];
    const existingAchievementIds = stats.achievements.map(a => a.id);

    // Define achievement criteria
    const achievements = [
      {
        id: 'first_sticker',
        name: this.getAchievementText('first_sticker', userLanguage).name,
        description: this.getAchievementText('first_sticker', userLanguage).description,
        icon: '🌟',
        requirements: { type: 'total_stickers' as const, value: 1 }
      },
      {
        id: 'sticker_collector_10',
        name: this.getAchievementText('sticker_collector_10', userLanguage).name,
        description: this.getAchievementText('sticker_collector_10', userLanguage).description,
        icon: '🎖️',
        requirements: { type: 'total_stickers' as const, value: 10 }
      },
      {
        id: 'sticker_master_50',
        name: this.getAchievementText('sticker_master_50', userLanguage).name,
        description: this.getAchievementText('sticker_master_50', userLanguage).description,
        icon: '🏆',
        requirements: { type: 'total_stickers' as const, value: 50 }
      },
      {
        id: 'sticker_legend_100',
        name: this.getAchievementText('sticker_legend_100', userLanguage).name,
        description: this.getAchievementText('sticker_legend_100', userLanguage).description,
        icon: '👑',
        requirements: { type: 'total_stickers' as const, value: 100 }
      },
      {
        id: 'first_plan',
        name: this.getAchievementText('first_plan', userLanguage).name,
        description: this.getAchievementText('first_plan', userLanguage).description,
        icon: '✅',
        requirements: { type: 'plans_completed' as const, value: 1 }
      },
      {
        id: 'plan_expert_5',
        name: this.getAchievementText('plan_expert_5', userLanguage).name,
        description: this.getAchievementText('plan_expert_5', userLanguage).description,
        icon: '🎯',
        requirements: { type: 'plans_completed' as const, value: 5 }
      },
      {
        id: 'streak_master_7',
        name: this.getAchievementText('streak_master_7', userLanguage).name,
        description: this.getAchievementText('streak_master_7', userLanguage).description,
        icon: '🔥',
        requirements: { type: 'streak' as const, value: 7 }
      },
      {
        id: 'streak_legend_30',
        name: this.getAchievementText('streak_legend_30', userLanguage).name,
        description: this.getAchievementText('streak_legend_30', userLanguage).description,
        icon: '⚡',
        requirements: { type: 'streak' as const, value: 30 }
      }
    ];

    // Check each achievement
    for (const achievement of achievements) {
      if (existingAchievementIds.includes(achievement.id)) {
        continue; // Already unlocked
      }

      let hasUnlocked = false;
      switch (achievement.requirements.type) {
        case 'total_stickers':
          hasUnlocked = stats.totalStickers >= achievement.requirements.value;
          break;
        case 'plans_completed':
          hasUnlocked = stats.totalPlansCompleted >= achievement.requirements.value;
          break;
        case 'streak':
          hasUnlocked = stats.currentStreak >= achievement.requirements.value;
          break;
      }

      if (hasUnlocked) {
        newAchievements.push({
          ...achievement,
          unlockedAt: new Date()
        });
      }
    }

    return newAchievements;
  }

  /**
   * Get default user stats for new users
   */
  private getDefaultUserStats(): UserStickerStats {
    return {
      totalStickers: 0,
      totalPlansCompleted: 0,
      totalDaysCompleted: 0,
      averageStickersPerPlan: 0,
      currentStreak: 0,
      longestStreak: 0,
      lastActivityDate: new Date(),
      stickersByPlanType: {
        dynamic: 0,
        template: 0,
        communication: 0
      },
      recentStickerEvents: [],
      achievements: []
    };
  }

  /**
   * Get leaderboard data (top users by sticker count)
   */
  async getStickerLeaderboard(limit: number = 10): Promise<Array<{
    userId: string;
    displayName: string;
    totalStickers: number;
    totalPlansCompleted: number;
    rank: number;
  }>> {
    try {
      // Note: This would require a more complex query in a real implementation
      // For now, we'll return a placeholder structure
      console.log('📊 Getting sticker leaderboard (placeholder)');
      return [];
    } catch (error) {
      console.error('❌ Error getting leaderboard:', error);
      return [];
    }
  }
}

export const stickerTrackingService = new StickerTrackingService();
